import React, { useEffect, useState } from 'react';
import ReactGA from 'react-ga4';
import { useAuthState } from '@vis-auth/react';
import { generatePath, Link, useLocation, useParams } from 'react-router-dom';
import LoaderIcon from 'uswds/dist/img/loader.svg';
import { AccountType, HiringSite } from 'eve-account-client';
import { Alert } from '@ver-uds/uswds-react';
import { EveRoutes } from '@ver-uds/e-verify';
import { SubmitHandler } from 'react-hook-form';
import FullWidthLayout from '../layouts/FullWidthLayout';
import {
  useAccountQuery,
  useHiringSitesBulkUploadMutation,
  useHiringSitesCreateMutation,
  useHiringSitesDeleteMutation,
  useHiringSitesPaginatedQuery,
  useHiringSitesUpdateMutation,
} from '../services';
import AlertConstants from '../constants/AlertConstants';
import FormVariant from '../components/Enrollment/FormVariant';
import ErrorConstants from '../constants/ErrorConstants';
import SubmitAction from '../components/Enrollment/SubmitAction';
import UploadErrors from '../components/HiringSites/BulkUpload/UploadErrors';
import { ValidationResult } from '../components/HiringSites/BulkUpload/HiringSiteValidator';
import HiringSitesForm from '../components/HiringSites/HiringSitesForm';
import DeleteLegacyHiringSitesModal from '../components/HiringSites/DeleteLegacy/DeleteLegacyHiringSitesModal';

const EditHiringSitesPage = (): JSX.Element => {
  const { user } = useAuthState();
  const { subAccountId } = useParams<{ subAccountId?: string }>();
  const [syncModalIsOpen, setSyncModalIsOpen] = useState(false);

  // eslint-disable-next-line camelcase
  const accountId = subAccountId || (user?.account_id as string);
  let doneUrl: string = EveRoutes.COMPANY_PROFILE_VIEW;

  const { data: accountData } = useAccountQuery(accountId);

  if (subAccountId) {
    const doneUrlPath =
      accountData?.type === AccountType.CHILD
        ? EveRoutes.CORPORATE_COMPANY_LOCATIONS_CHILD_PROFILE_VIEW
        : EveRoutes.COMPANY_CLIENTS_CLIENT_PROFILE_VIEW;
    doneUrl = generatePath(doneUrlPath, { subAccountId: accountId });
  }

  const { isLoading, resolvedData } = useHiringSitesPaginatedQuery(accountId);

  const [modalErrorMessage, setModalErrorMessage] = useState<string | undefined>(undefined);
  const [bulkUploadErrors, setBulkUploadErrors] = useState<HiringSite[] | undefined>(undefined);
  const [uploadResults, setUploadResults] = useState<ValidationResult | undefined>(undefined);
  const pageTitle = 'Edit Hiring Sites';

  const legacyCount = resolvedData?.states.reduce((total, { count }) => total + count, 0) || 0;

  const location = useLocation();
  const removedLegacySites = Number(location.state || 0);
  const totalHiringSites = resolvedData?.paginated?.totalElements || 0;
  const legacyCountToRemove = (legacyCount || 0) - totalHiringSites;
  if (!syncModalIsOpen && legacyCountToRemove > 0 && removedLegacySites === 0) {
    setSyncModalIsOpen(true);
  } else if (syncModalIsOpen && removedLegacySites > 0) {
    setSyncModalIsOpen(false);
  }

  // Create hiring site
  const [mutateCreate] = useHiringSitesCreateMutation(() => {
    setModalErrorMessage(ErrorConstants.DUPLICATE_HIRING_SITES);
  });
  const onAddSubmit: SubmitHandler<HiringSite> = async (data) => {
    try {
      await mutateCreate({
        accountId,
        hiringSite: data,
      });
      const message = modalErrorMessage === '' ? undefined : ''; // to ensure update triggers in form
      setModalErrorMessage(message);
      setUploadResults({ validSites: [data], invalidSites: [] });
    } catch (error) {
      // prevent closing the modal if the submit failed so we can display the error
    }
  };

  // Bulk Upload Hiring Sites
  const [mutateBulkUpload] = useHiringSitesBulkUploadMutation(() => {
    setUploadResults({ validSites: [], invalidSites: [], errorMessage: ErrorConstants.GENERAL_ERROR });
  });
  const onBulkAddSubmit: SubmitHandler<HiringSite[]> = async (data) => {
    try {
      await mutateBulkUpload({
        accountId,
        hiringSites: data,
      });
      const message = modalErrorMessage === '' ? undefined : ''; // to ensure update triggers in form
      setModalErrorMessage(message);
      setUploadResults({ validSites: data, invalidSites: [] });
    } catch (error) {
      // prevent closing the modal if the submit failed so we can display the error
    }
  };

  // Update hiring site
  const [mutateUpdate] = useHiringSitesUpdateMutation((error: Error | Response) => {
    setModalErrorMessage(ErrorConstants.DUPLICATE_HIRING_SITES);
  });
  const onUpdateSubmit: SubmitHandler<HiringSite> = async (data) => {
    try {
      await mutateUpdate({
        accountId,
        hiringSiteId: data.id as string,
        hiringSite: data,
      });
      const message = modalErrorMessage === '' ? undefined : ''; // to ensure update triggers in form
      setModalErrorMessage(message);
      setUploadResults(undefined);
    } catch (error) {
      // prevent closing the modal if the submit failed so we can display the error
    }
  };

  // Delete hiring site
  const [mutateDelete] = useHiringSitesDeleteMutation();
  const onDeleteSubmit: SubmitHandler<HiringSite> = async (data) => {
    if (data && data.id) {
      await mutateDelete({
        accountId,
        hiringSiteId: data.id,
      });
      setUploadResults(undefined);
    }
  };

  const onSubmit = (action: SubmitAction, hiringSites: HiringSite[], uploadErrors: HiringSite[]) => {
    if (action === SubmitAction.ADD) {
      onAddSubmit(hiringSites[0]);
    }
    if (action === SubmitAction.BULK_UPLOAD) {
      setBulkUploadErrors(uploadErrors);
      onBulkAddSubmit(hiringSites);
    }
    if (action === SubmitAction.UPDATE) {
      onUpdateSubmit(hiringSites[0]);
    }
    if (action === SubmitAction.DELETE) {
      onDeleteSubmit(hiringSites[0]);
    }
  };
  useEffect(() => {
    ReactGA.send({
      hitType: 'pageview',
      pageTitle: 'E-Verify - Edit Hiring Sites',
    });
  }, []);
  return (
    <FullWidthLayout title={pageTitle}>
      <section className="padding-y-4">
        <h1>{pageTitle}</h1>
        {removedLegacySites ? (
          <Alert status="warning" slim>
            <Alert.Text>
              {AlertConstants.REMOVED_SITES.replace('{removedLegacySites}', String(removedLegacySites))}
            </Alert.Text>
          </Alert>
        ) : (
          ''
        )}
        <p className="margin-top-3 margin-bottom-3">
          A hiring site is the location where employees are hired and they complete Form I-9.
        </p>
        {isLoading || !resolvedData ? (
          <div className="margin-top-5 margin-bottom-5">
            Loading… <img src={LoaderIcon} alt="" className="height-2" />
          </div>
        ) : (
          <>
            <HiringSitesForm
              variant={FormVariant.EDIT}
              paginatedData={resolvedData}
              errorMessage={modalErrorMessage}
              onSubmit={onSubmit}
              onAddOrEditModalClose={() => setModalErrorMessage('')}
              UploadErrors={() => (
                <UploadErrors
                  variant={FormVariant.EDIT}
                  uploadErrors={bulkUploadErrors}
                  uploadResults={uploadResults}
                />
              )}
            />
            <DeleteLegacyHiringSitesModal
              open={syncModalIsOpen}
              close={(): void => {
                setSyncModalIsOpen(false);
              }}
              numberOfLegacySites={legacyCountToRemove}
              sitesByState={resolvedData?.states}
            />
            <Link to={doneUrl || ''} className="usa-button usa-button--primary margin-top-5">
              Done
            </Link>
          </>
        )}
      </section>
    </FullWidthLayout>
  );
};

export default EditHiringSitesPage;
